import JSZip from "jszip";
import { necCountryCodes } from "../../../models/nec";
import { IBranch } from "../../../store/cgmBranchesReducer";
import { ITimestamp, KeyValue } from "../../TimestampsSelector/timestamps";
import { LtccMonthlyYearly } from "../../../store/validationProcess";
import { formatDate } from "../../CalendarInput/formatDate";
import { toast } from "../../..";

export interface IOption {
    value: string;
    text: string;
}

export const extractLinesCB = async (
    zipFile: File,
    activeTimestamps: ITimestamp[],
    country: string
): Promise<{
    branches: IOption[];
    neFilters: IOption[];
    cFilters: IOption[];
}> => {
    const inputZip = new JSZip();
    const topLvlZip = await inputZip.loadAsync(zipFile);
    const topLvlFiles = Object.keys(topLvlZip.files);
    let cbFile = topLvlFiles.find(f => f.includes("F301") && f.endsWith(".xml"));
    if (!cbFile) cbFile = topLvlFiles.find(f => f.includes("F312") && f.endsWith(".xml"));
    if (!cbFile) cbFile = topLvlFiles.find(f => f.includes("-666-") && f.endsWith(".xml"));
    if (!cbFile) cbFile = topLvlFiles.find(f => f.includes("-665-") && f.endsWith(".xml"));
    let cbFiles = [cbFile];
    if (!cbFile && activeTimestamps) {
        const insideZip = true;
        if (insideZip) {
            let f110file = topLvlFiles.find(f => f.includes("301") && f.endsWith(".zip"));
            const loaded110 = await topLvlZip.file(f110file!)!.async("blob");
            const loadedSubZip = await inputZip.loadAsync(loaded110);
            const downLevelFiles = Object.keys(loadedSubZip.files);
            //fetch LTCC scenario files
            cbFiles = downLevelFiles.filter(f => f.includes("301") && activeTimestamps.map(d => d.text).some(ff => f.includes(ff)) && f.endsWith(".xml"));
        }
    }
    const branches: IOption[] = [];
    const neFilters: string[] = [];
    const cFilters: string[] = [];
    /*
    const str = await topLvlZip.file(cbFile!)!.async("string");
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(str, "text/xml");
    const branchNodes = xmlDoc.getElementsByTagName("criticalBranch");
    */
    let branchNodes: Element[] = [];
    for (let cbFileTemp of cbFiles) {
        const str = await topLvlZip.file(cbFileTemp!)!.async("string");
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(str, "text/xml");
        let tempNodes = xmlDoc.getElementsByTagName("criticalBranch");
        branchNodes.push(...Array.from(tempNodes));
    }

    let lastText = "";
    for (const item of branchNodes) {
        const tsoOrigin = item.getElementsByTagName("tsoOrigin")[0].textContent;
        const id = item.getAttribute("id")!;
        if (tsoOrigin!.trim() !== country) continue;
        const name = item.getElementsByTagName("branch")[0].getAttribute("name")!;
        const outageElements = item.getElementsByTagName("outage");
        const outage =
            outageElements.length > 0 ? outageElements[0].getAttribute("name") : "base case";
        const text = `${name} | ${outage}`;
        if (text !== lastText) {
            branches.push({ value: id, text: text });
            neFilters.push(name);
            cFilters.push(outage!);
            lastText = text;
        }
    }
    const uniqNeFilters = [...new Set(neFilters)];
    const uniqCFilters = [...new Set(cFilters)];
    const uniqueBranches = Array.from(new Set(branches.map(obj => JSON.stringify(obj))))
        .map(str => JSON.parse(str) as IOption);
    return {
        branches: uniqueBranches,
        neFilters: uniqNeFilters.map(x => ({ value: x, text: x })),
        cFilters: uniqCFilters.map(x => ({ value: x, text: x })),
    };
};

export const extractLinesCGM = async (
    zipFile: File,
    country: string,
    timestamps: ITimestamp[],
    lort: string,
    voltage?: string,
    searchString?: string
): Promise<IBranch[]> => {
    const promisesArray: Promise<IBranch[]>[] = [];
    const inputZip = new JSZip();
    const topLvlZipFiles = await inputZip.loadAsync(zipFile);
    const topLvlFiles = Object.keys(topLvlZipFiles.files);
    const CGMZip = new JSZip();
    let cgmFile = topLvlFiles.find(f => f.includes("F100") && f.endsWith(".zip"));
    if (!cgmFile) cgmFile = topLvlFiles.find(f => f.includes("F109") && f.endsWith(".zip"));
    if (!cgmFile) cgmFile = topLvlFiles.find(f => f.includes("F308") && f.endsWith(".zip"));
    if (!cgmFile) cgmFile = topLvlFiles.find(f => f.includes("FID2-620") && f.endsWith(".zip"));
    if (!cgmFile) cgmFile = topLvlFiles.find(f => (f.includes("LT-001") || f.includes("LT-002")) && f.endsWith(".zip"));
    const cgmFileZip = await topLvlZipFiles.files[cgmFile!].async("arraybuffer");
    const f100Files = await CGMZip.loadAsync(cgmFileZip);
    const allCGMFiles = Object.keys(f100Files.files);
    for (const t in timestamps) {
        promisesArray.push(
            sendHelp(
                Number.parseInt(t),
                timestamps,
                allCGMFiles,
                f100Files,
                lort,
                country,
                voltage,
                searchString
            )
        );
    }
    const killMe = await Promise.all(promisesArray);
    return killMe.flat();
};

const sendHelp = async (
    t: number,
    timestamps: ITimestamp[],
    allCGMFiles: string[],
    f100Files: JSZip,
    lort: string,
    country: string,
    voltage?: string,
    searchString?: string
): Promise<IBranch[]> => {
    return new Promise((resolveTopLvl, reject) => {
        const tempResult: IBranch[] = [];
        //const timestamp = String(timestamps[t]).padStart(2, "0") + "30";
        const timestamp = timestamps[t].cgmtimestamp;
        const fileToAnalyze = allCGMFiles.find(file => file.includes(timestamp));
        if (fileToAnalyze !== null) {
            f100Files
                .file(fileToAnalyze!)!
                .async("string")
                .then((str: string) => {
                    return new Promise(resolve => {
                        let fileParts;
                        if (lort === "##L") fileParts = str.split("##L");
                        else fileParts = str.split("##T");
                        const remainingNetworkParts = fileParts[1].split("##");
                        const neededLines = remainingNetworkParts[0].split("\n");
                        for (const line in neededLines) {
                            if (neededLines[line].length !== 1) {
                                const nodeCode = neededLines[line].substring(0, 8);
                                const nodeCode2 = neededLines[line].substring(9, 17);
                                const voltageChar = nodeCode.charAt(6);
                                const voltageChar2 = nodeCode2.charAt(6);
                                let elementName;
                                if (lort === "##L")
                                    elementName = neededLines[line].substring(51, 64);
                                else elementName = neededLines[line].substring(77, 89);
                                let condition =
                                    country === "XX"
                                        ? nodeCode.startsWith(necCountryCodes[country]) &&
                                          nodeCode2.startsWith(necCountryCodes[country]) &&
                                          (voltageChar === "0" ||
                                              voltageChar === "1" ||
                                              voltageChar === "2" ||
                                              voltageChar === "8" ||
                                              voltageChar === "9") &&
                                          (neededLines[line].charAt(21) !== "7" ||
                                              neededLines[line].charAt(21) !== "8" ||
                                              neededLines[line].charAt(21) !== "9")
                                        : (nodeCode.startsWith(necCountryCodes[country]) ||
                                              nodeCode2.startsWith(necCountryCodes[country])) &&
                                          (voltageChar === "0" ||
                                              voltageChar === "1" ||
                                              voltageChar === "2" ||
                                              voltageChar === "8" ||
                                              voltageChar === "9") &&
                                          (neededLines[line].charAt(21) !== "7" ||
                                              neededLines[line].charAt(21) !== "8" ||
                                              neededLines[line].charAt(21) !== "9");

                                if (voltage) {
                                    condition =
                                        nodeCode.startsWith(necCountryCodes[country]) &&
                                        (voltageChar === voltage || voltageChar2 === voltage) &&
                                        (neededLines[line].charAt(21) !== "7" ||
                                            neededLines[line].charAt(21) !== "8" ||
                                            neededLines[line].charAt(21) !== "9");
                                }
                                if (searchString) {
                                    const search =
                                        nodeCode.toLowerCase().includes(searchString) ||
                                        nodeCode2.toLowerCase().includes(searchString) ||
                                        elementName.toLowerCase().includes(searchString);
                                    condition = condition && search;
                                }
                                if (condition) {
                                    const newLine: IBranch = {
                                        timestamp:
                                        timestamps[t].text,
                                        /*
                                            timestamp.substring(0, 2) +
                                            ":" +
                                            timestamp.substring(2),
                                            */
                                        node1: nodeCode,
                                        node2: neededLines[line].substring(9, 17),
                                        orderCode: neededLines[line].charAt(18),
                                        elementName: elementName,
                                    };
                                    tempResult.push(newLine);
                                }
                            }
                        }
                        resolve(tempResult);
                    });
                })
                .then(res => resolveTopLvl(tempResult));
        } else reject();
    });
};

export const parseLtccXmlFromZip = async (
    zipFile: File,
): Promise<{
    ltccMonthlyYearly: LtccMonthlyYearly;
    calculationDate: string;
    availableScenarios: KeyValue[];
}> => {
    const inputZip = new JSZip();
    const topLvlZipFiles = await inputZip.loadAsync(zipFile);
    const topLvlFiles = Object.keys(topLvlZipFiles.files);
    const CGMZip = new JSZip();
    let cgmFile = topLvlFiles.find(f => (f.includes("LT-001") || f.includes("LT-002")) && f.endsWith(".zip"));
    if (!cgmFile) {
        return {
            ltccMonthlyYearly: LtccMonthlyYearly.Unknown,
            calculationDate: "",
            availableScenarios: [],
        };
    }
    const cgmFileZip = await topLvlZipFiles.files[cgmFile!].async("arraybuffer");
    const f100Files = await CGMZip.loadAsync(cgmFileZip);
    const allCGMFiles = Object.keys(f100Files.files);
    const verticesFile = allCGMFiles.find((f) => f.includes("LT-001") || f.includes("LT-002"));

    let ltccMonthlyYearly = LtccMonthlyYearly.Unknown;
    let calculationDate = "";

    if (verticesFile) {
        const file = await f100Files
            .file(verticesFile)!
            .async("blob")
            .then((fileData) => {
                return new File([fileData], verticesFile);
            });
        return new Promise<{
            ltccMonthlyYearly: LtccMonthlyYearly;
            calculationDate: string;
            availableScenarios: KeyValue[];
        }>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const parser = new DOMParser();
                const xml = parser.parseFromString(e.target.result, 'text/xml');
                const fg = xml.querySelectorAll("TimestampsDefinition");
                const timeInterval = fg[0].getAttribute("timeInterval");

                let lists = fg[0].querySelectorAll("Timestamp");
                //let availableScenarios: string[] = [];
                let availableScenarios: KeyValue[] = [];

                lists.forEach(function (value) {
                    if (value) {
                        let idname = value.querySelector('Id')?.firstChild;
                        if (idname?.textContent) {
                            const descriptionPoputText = "Name " + value.querySelector('Name')?.firstChild?.textContent + " Description: " + value.querySelector('Description')?.firstChild?.textContent + " TimeFrame: " + value.querySelector('TimeFrame')?.firstChild?.textContent + " Type: " + value.querySelector('Type')?.firstChild?.textContent + " Selection: " + value.querySelector('Selection')?.firstChild?.textContent;
                            //availableScenarios.push(idname.textContent);
                            availableScenarios.push({ key: idname.textContent, value: descriptionPoputText });
                        }
                    }
                });
                if (timeInterval) {
                    const startTime = new Date(timeInterval.split("/")[0]);
                    const endTime = new Date(timeInterval.split("/")[1]);

                    if (startTime.getFullYear() === endTime.getFullYear()) {
                        //monthly
                        ltccMonthlyYearly = LtccMonthlyYearly.Monthly;
                        calculationDate = formatDate(new Date(startTime.getFullYear() + "/" + (startTime.getMonth() + 1) + "-1"));

                    }
                    else {
                        //yearly
                        ltccMonthlyYearly = LtccMonthlyYearly.Yearly;
                        calculationDate = formatDate(new Date(startTime.getFullYear() + "-1-1"));
                    }
                }
                resolve({
                    ltccMonthlyYearly,
                    calculationDate,
                    availableScenarios,
                })
            };
            reader.readAsBinaryString(file);
        });
    }
    else {
        return {
            ltccMonthlyYearly,
            calculationDate,
            availableScenarios: []
        };
    }
};

export const getFileListFromZip = async (
    zipFile: File,
    hideToast?: boolean,
): Promise<{
    fileNames: string[];
    containsFID3Cfiles: boolean;
    containsFID3files: boolean;
    containsFID3Efiles: boolean;
    containsAHCexternalConfiguration: boolean;
}> => {
    const inputZip = new JSZip();
    const topLvlZipFiles = await inputZip.loadAsync(zipFile);
    const topLvlFiles = Object.keys(topLvlZipFiles.files);

    const fid3 = topLvlFiles.find(f => (f.includes("-FID3-")));
    const fid3c = topLvlFiles.find(f => (f.includes("-FID3C-")));
    const fid3e = topLvlFiles.find(f => (f.includes("-FID3E-")));
    const fid2 = topLvlFiles.find(f => (f.includes("-FID2-")));
    if (!hideToast && fid3c && fid2 && fid3 && fid3e) {
        toast("Error in input file: The file contains data for multiple ID subprocesses, but only one is expected. Please ensure that the file includes data for a single subprocess!", false);
    }
    const f326 = topLvlFiles.find(f => (f.includes("F326")));
    const f327 = topLvlFiles.find(f => (f.includes("F327")));

    return {
        fileNames: topLvlFiles,
        containsAHCexternalConfiguration: !!f326 || !!f327,
        containsFID3files: !!fid3,
        containsFID3Cfiles: !!fid3c,
        containsFID3Efiles: !!fid3e,
    };
};